import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, ScanLine, CheckCircle2, XCircle, ShieldAlert,
  Loader2, MapPin, Ticket, Users, Camera, CameraOff, Keyboard
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const SCANNER_ID = 'qr-scanner-region';

const BusOperatorScan = () => {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const [boarding, setBoarding] = useState(false);
  const [boarded, setBoarded] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      navigate('/bus/operator/login', { state: { returnTo: '/bus/operator/scan' } });
    }
    return () => { stopScanner(); };
  }, [navigate]);

  const verify = async (payload) => {
    setVerifying(true);
    setBoarded(false);
    try {
      const res = await api.post('/bus/tickets/verify', payload);
      setResult(res.data);
    } catch (e) {
      toast({ title: 'Verification failed', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setVerifying(false);
    }
  };

  const confirmBoarding = async () => {
    if (!result?.ticketCode) return;
    setBoarding(true);
    try {
      const res = await api.post('/bus/tickets/board', { ticket_code: result.ticketCode });
      if (res.data.boarded) {
        setBoarded(true);
        toast({ title: 'Boarding confirmed', description: `${result.ticketCode} marked as boarded.` });
      } else if (res.data.alreadyBoarded) {
        setResult((r) => ({ ...r, valid: false, reason: 'already_boarded', alreadyBoarded: true }));
        toast({ title: 'Already boarded', description: 'This ticket was already used.', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Could not confirm', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setBoarding(false);
    }
  };

  const onScanSuccess = async (decodedText) => {
    await stopScanner();
    verify({ qr_data: decodedText });
  };

  const startScanner = async () => {
    setResult(null);
    setScanning(true);
    try {
      const scanner = new Html5Qrcode(SCANNER_ID);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        onScanSuccess,
        () => {} // ignore per-frame decode errors
      );
    } catch (e) {
      setScanning(false);
      toast({
        title: 'Camera unavailable',
        description: 'Allow camera access, or enter the ticket code manually below.',
        variant: 'destructive',
      });
    }
  };

  const stopScanner = async () => {
    const scanner = scannerRef.current;
    if (scanner) {
      try {
        if (scanner.isScanning) await scanner.stop();
        await scanner.clear();
      } catch (_) { /* noop */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const handleManual = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    verify({ ticket_code: manualCode.trim() });
  };

  const reset = () => { setResult(null); setManualCode(''); setBoarded(false); };

  const banner = () => {
    if (!result) return null;
    if (result.valid) {
      return { color: 'green', Icon: CheckCircle2, title: 'Valid ticket — allow boarding' };
    }
    if (result.reason === 'invalid_signature') {
      return { color: 'red', Icon: ShieldAlert, title: 'Forged / tampered QR — do not board' };
    }
    if (result.reason === 'cancelled') {
      return { color: 'red', Icon: XCircle, title: 'Ticket cancelled — do not board' };
    }
    if (result.reason === 'already_boarded') {
      return { color: 'amber', Icon: ShieldAlert, title: 'Already boarded — duplicate scan' };
    }
    if (result.reason === 'not_found') {
      return { color: 'red', Icon: XCircle, title: 'No such ticket' };
    }
    return { color: 'red', Icon: XCircle, title: 'Invalid ticket' };
  };

  const b = banner();
  const colorMap = {
    green: 'bg-green-50 border-green-300 text-green-800',
    red: 'bg-red-50 border-red-300 text-red-800',
    amber: 'bg-amber-50 border-amber-300 text-amber-800',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/bus/operator')} data-testid="scan-back-btn" className="text-gray-500 hover:text-gray-800">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <ScanLine className="w-6 h-6 text-indigo-600" />
          <div>
            <h1 className="text-lg font-bold leading-tight">Scan Ticket</h1>
            <p className="text-xs text-gray-500">Verify passengers at the boarding gate</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {/* Scanner */}
        <Card className="p-5">
          <div id={SCANNER_ID} className="w-full rounded-lg overflow-hidden bg-black/5 min-h-[60px]" data-testid="scanner-region" />
          <div className="mt-4 flex gap-2">
            {!scanning ? (
              <Button onClick={startScanner} className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="start-scan-btn">
                <Camera className="w-4 h-4 mr-2" /> Start camera scan
              </Button>
            ) : (
              <Button onClick={stopScanner} variant="outline" className="flex-1" data-testid="stop-scan-btn">
                <CameraOff className="w-4 h-4 mr-2" /> Stop camera
              </Button>
            )}
          </div>
        </Card>

        {/* Manual entry */}
        <Card className="p-5">
          <p className="text-sm font-medium flex items-center gap-2 mb-3">
            <Keyboard className="w-4 h-4 text-gray-500" /> Enter ticket code manually
          </p>
          <form onSubmit={handleManual} className="flex gap-2">
            <Input value={manualCode} onChange={(e) => setManualCode(e.target.value)}
              placeholder="TK-BUS-XXXXXX" className="font-mono" data-testid="manual-code-input" />
            <Button type="submit" disabled={verifying} data-testid="manual-verify-btn">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
            </Button>
          </form>
        </Card>

        {/* Result */}
        {b && (
          <Card className={`p-5 border-2 ${colorMap[b.color]}`} data-testid="scan-result">
            <div className="flex items-center gap-3 mb-4">
              <b.Icon className="w-8 h-8 shrink-0" />
              <div>
                <p className="font-bold text-base" data-testid="scan-result-title">{b.title}</p>
                <p className="text-sm font-mono opacity-80">{result.ticketCode}</p>
              </div>
            </div>

            {result.ticketCode && result.reason !== 'not_found' && (
              <div className="bg-white/60 rounded-lg p-4 space-y-2 text-gray-800">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  {result.boarding?.city || result.route?.origin?.city} → {result.alighting?.city || result.route?.destination?.city}
                  {result.date ? <span className="text-gray-500">· {result.date}</span> : null}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-gray-500" />
                  {result.passengerCount} passenger(s) · seats {Array.isArray(result.seats) ? result.seats.join(', ') : '—'}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Ticket className="w-4 h-4 text-gray-500" />
                  Status: <span className="font-medium">{result.status}</span> · Payment: <span className="font-medium">{result.paymentStatus}</span>
                </div>
                {result.signatureValid === false && result.reason !== 'invalid_signature' && (
                  <p className="text-xs text-amber-700">⚠ Legacy unsigned ticket — verify ID manually.</p>
                )}
                {result.operatorName && <p className="text-xs text-gray-500">{result.operatorName}</p>}
              </div>
            )}

            {result.valid && !boarded && (
              <Button onClick={confirmBoarding} disabled={boarding}
                className="w-full mt-4 bg-green-600 hover:bg-green-700" data-testid="confirm-boarding-btn">
                {boarding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Confirm boarding
              </Button>
            )}

            {boarded && (
              <div className="w-full mt-4 flex items-center justify-center gap-2 text-green-700 font-medium" data-testid="boarded-confirmed">
                <CheckCircle2 className="w-4 h-4" /> Boarded ✓
              </div>
            )}

            <Button onClick={reset} variant="outline" className="w-full mt-4 bg-white" data-testid="scan-next-btn">
              Scan next ticket
            </Button>
          </Card>
        )}      </div>
    </div>
  );
};

export default BusOperatorScan;
